<script lang="ts">
  import type { SettlementDetails, BankMatch } from "../types";
  import { formatGbp } from "../../../lib/formatting";
  import Check from "~icons/ph/check-bold";
  import Info from "~icons/ph/info-bold";
  import FooterButtons from "../../../components/workflow/FooterButtons.svelte";
  import OfferBreakdown from "../elements/OfferBreakdown.svelte";
  import BankAccountInfo from "../../Settlement/elements/BankAccountInfo.svelte";
  import {
    FaqHowCalculated,
    FaqWhenPayment,
    FaqHowPayment,
    FaqMultipleSettlements,
    FaqWhyFees,
    FaqTaxable,
  } from "../faqs/Faq";
  import DownloadLink from "../../Settlement/elements/DownloadLink.svelte";
  import CloseButton from "../../../components/CloseButton.svelte";

  let {
    settlementDetails,
    bankMatch,
    jlgMainPhone,
    reopened,
    nextSettlementTaskUrl,
  }: {
    settlementDetails: SettlementDetails;
    bankMatch: BankMatch;
    jlgMainPhone: string;
    reopened: boolean;
    nextSettlementTaskUrl: string | null;
  } = $props();
</script>

<div class="relative isolate">
  {#if reopened}
    <h2 class="ui-title">Your payment is on the way</h2>

    <p>Here’s where things stand:</p>
  {:else}
    <div
      class="pointer-events-none absolute -z-10 -m-4 inset-x-0 top-0 h-[313px] bg-gradient-to-b from-sky-200/40"
      aria-hidden="true">
    </div>

    <div class="flex justify-center py-6">
      <div class="bg-white w-16 h-16 rounded-full flex items-center justify-center shadow">
        <Check class="text-emerald-500 w-11 h-11" />
      </div>
    </div>

    <h2 class="ui-title">You’ll receive payment soon</h2>

    <p>Now that you've connected your bank account, here's what happens next:</p>
  {/if}

  <ol class="payment-steps mb-6">
    <li><span>1</span>We’ll notify the finance company that you’ve accepted the offer</li>
    <li><span>2</span>They’ll send the payment to us</li>
    <li><span>3</span>Then we’ll deposit it into your bank account</li>
  </ol>

  <OfferBreakdown {settlementDetails} class="mb-6">
    <div class="ui-alert ui-alert-info">
      <Info />
      You accepted the offer, and agreed to the deduction of JLG’s fee of {formatGbp(settlementDetails.jlgFeeAmount)} from
      your compensation as payment.
    </div>
  </OfferBreakdown>

  <BankAccountInfo {bankMatch} showHeader={true} />

  <h3 class="ui-section-title">Have questions?</h3>
  <div class="ui-accordion">
    <FaqHowCalculated />
    <FaqWhenPayment />
    <FaqHowPayment phone={jlgMainPhone} />
    <FaqMultipleSettlements />
    <FaqWhyFees gross={settlementDetails.grossSettlementAmount} />
    <FaqTaxable />
  </div>

  <DownloadLink label="Download statute bill" href={settlementDetails.statuteBillUrl} class="mb-4" />
  <DownloadLink label="Download letter of advice" href={settlementDetails.letterOfAdviceUrl} />

  <FooterButtons back={false}>
    {#if nextSettlementTaskUrl}
      <CloseButton />
      <a href={nextSettlementTaskUrl} class="btn btn-primary" data-turbo-frame="task-modal">Next settlement</a>
    {:else}
      <CloseButton class="btn-primary w-full" />
    {/if}
  </FooterButtons>
</div>

<style lang="postcss">
  .payment-steps {
    @apply flex flex-col gap-4;

    li {
      @apply flex flex-row gap-2;
    }

    li > span {
      @apply shrink-0 bg-gray-200 w-8 h-8 rounded-full inline-flex items-center justify-center font-bold -mt-1;
    }
  }
</style>
